import { useState } from "react";

import { Trans } from "@lingui/react/macro";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Fingerprint, Loader2, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { PasskeyCard } from "@/components/admin/security/passkey-card";
import { TrustedDevicesCard } from "@/components/admin/security/trusted-devices-card";
import { TwoFactorCard } from "@/components/admin/security/two-factor-card";

import { authenticateWithPasskey } from "@/lib/webauthn";

/**
 * Admin → Security.
 *
 * Port of `apps/app/src/routes/_authenticated/account/user/security.tsx` — flat
 * card layout (no tabs) suitable for the desktop-first admin shell.
 *
 * Endpoints (all live in apps/api):
 *   - GET    /api/account/2fa/status
 *   - POST   /api/account/2fa/enroll/{start,verify}
 *   - POST   /api/account/2fa/disable
 *   - POST   /api/account/2fa/recovery/regenerate
 *   - GET    /api/account/2fa/trusted-devices
 *   - DELETE /api/account/2fa/trusted-devices/:id
 *   - GET    /api/account/vault-security/biometric/credentials
 *   - PATCH  /api/account/vault-security/biometric/credentials
 *   - DELETE /api/account/vault-security/biometric/credentials  (id in body)
 *   - POST   /api/account/vault-security/biometric/register/{options,verify}
 *   - POST   /api/account/vault-security/biometric/auth/{options,verify}
 */
export const Route = createFileRoute("/_authenticated/security")({
  component: SecurityPage,
});

function SecurityPage() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-foreground text-2xl font-bold tracking-tight">
          <Trans>Security</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Protect your admin account with 2FA, passkeys, and trusted devices.</Trans>
        </p>
      </header>

      <TwoFactorCard />
      <PasskeyCard />
      <TrustedDevicesCard />
      <SelfTestCard />
    </div>
  );
}

function SelfTestCard() {
  const [state, setState] = useState<"idle" | "running" | "success" | "cancelled" | "error">(
    "idle",
  );
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const run = async () => {
    setState("running");
    setErrMsg(null);
    try {
      const ok = await authenticateWithPasskey("vault");
      setState(ok ? "success" : "cancelled");
    } catch (err) {
      setState("error");
      setErrMsg(err instanceof Error ? err.message : "unknown");
    }
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 py-4">
        <div className="flex items-start gap-3">
          <div className="bg-muted text-muted-foreground flex size-11 shrink-0 items-center justify-center rounded-full">
            <ShieldAlert className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-foreground text-base font-semibold">
              <Trans>Test your passkey</Trans>
            </h2>
            <p className="text-muted-foreground mt-1 text-xs">
              <Trans>
                Run an unlock prompt against your registered passkeys to confirm everything works.
              </Trans>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="lg"
            variant="outline"
            className="gap-1.5"
            onClick={run}
            disabled={state === "running"}
          >
            {state === "running" ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Fingerprint className="size-4" />
            )}
            <Trans>Unlock with passkey</Trans>
          </Button>

          {state === "success" && (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400">
              <Check className="size-4" />
              <Trans>Looks good — passkey works.</Trans>
            </span>
          )}
          {state === "cancelled" && (
            <span className="text-muted-foreground text-sm">
              <Trans>Cancelled.</Trans>
            </span>
          )}
          {state === "error" && (
            <span className="text-destructive text-sm">
              <Trans>Failed: {errMsg ?? "unknown"}</Trans>
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
